import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, api } from '../api/client';
import { InfoPanel } from '../components/InfoPanel';
import type { MbtilesEntry } from '../api/types';

export function MbtilesPage() {
  const [items, setItems] = useState<MbtilesEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const list = await api.listMbtiles();
      setItems(list);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <>
      <h2>mbtiles</h2>
      <InfoPanel
        what={
          <p style={{ margin: 0 }}>
            서버가 디스크에서 스캔해 들고 있는 mbtiles 카탈로그입니다. 업로드,
            public/private 토글, 표시 이름 변경, 삭제를 여기서 합니다.
          </p>
        }
        why={
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              파일시스템이 source of truth — 디스크에 새 mbtiles 가 들어오면 자동
              감지되지만, UI 에서 업로드·삭제하는 게 가장 안전한 경로입니다.
            </li>
            <li>
              public 토글로 익명 접근을 켜고 끌 수 있습니다. private 으로 두면
              read-token 의 allow 목록에 포함된 자만 볼 수 있습니다.
            </li>
            <li>
              새 mbtiles 업로드 후 <code>/tiles/:id</code> 가 404 면 정상 — tileserver-gl
              v5 한계로 <code>docker compose restart tileserver</code> 가 필요합니다.
            </li>
          </ul>
        }
      />
      {error && (
        <div className="banner error" data-testid="mbtiles-error">
          {error}
        </div>
      )}
      {info && (
        <div className="banner success" data-testid="mbtiles-info">
          {info}
        </div>
      )}

      <UploadCard
        onSuccess={(msg) => {
          setInfo(msg);
          setError(null);
          refresh();
        }}
        onError={(msg) => setError(msg)}
      />

      <div className="card">
        <h3>목록</h3>
        {!items ? (
          <div>로딩 중…</div>
        ) : items.length === 0 ? (
          <div>등록된 mbtiles가 없습니다.</div>
        ) : (
          <table data-testid="mbtiles-table">
            <thead>
              <tr>
                <th>id</th>
                <th>표시 이름</th>
                <th>공개</th>
                <th>파일명</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <Row
                  key={m.id}
                  entry={m}
                  onChange={(msg) => {
                    setInfo(msg);
                    setError(null);
                    refresh();
                  }}
                  onError={(msg) => setError(msg)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function UploadCard({
  onSuccess,
  onError,
}: {
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [id, setId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const result = await api.uploadMbtiles({
        file,
        id: id || undefined,
        displayName: displayName || undefined,
        isPublic,
      });
      onSuccess(`업로드 성공: ${result.id}`);
      setFile(null);
      setId('');
      setDisplayName('');
      setIsPublic(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const msg =
        err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message;
      onError(`업로드 실패 — ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3>새 mbtiles 업로드</h3>
      <form onSubmit={handleSubmit} className="row" data-testid="upload-form">
        <input
          type="file"
          accept=".mbtiles"
          ref={fileInputRef}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          data-testid="upload-file"
        />
        <input
          type="text"
          placeholder="id (생략 시 파일명에서 추출)"
          value={id}
          onChange={(e) => setId(e.target.value)}
          data-testid="upload-id"
        />
        <input
          type="text"
          placeholder="표시 이름 (선택)"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          data-testid="upload-displayName"
        />
        <label className="row" style={{ gap: '0.4rem' }}>
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            data-testid="upload-public"
          />
          공개
        </label>
        <button className="btn" type="submit" disabled={!file || busy} data-testid="upload-submit">
          {busy ? '업로드 중…' : '업로드'}
        </button>
      </form>
    </div>
  );
}

function Row({
  entry,
  onChange,
  onError,
}: {
  entry: MbtilesEntry;
  onChange: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  const togglePublic = async () => {
    setBusy(true);
    try {
      await api.updateMbtilesMeta(entry.id, { public: !entry.public });
      onChange(`${entry.id} → ${!entry.public ? '공개' : '비공개'}`);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(`${entry.id}를 삭제할까요? 파일과 메타가 함께 지워집니다.`)) return;
    setBusy(true);
    try {
      await api.deleteMbtiles(entry.id);
      onChange(`삭제됨: ${entry.id}`);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr data-testid={`mbtiles-row-${entry.id}`}>
      <td>
        <span className="code">{entry.id}</span>
      </td>
      <td>{entry.displayName}</td>
      <td>
        <span
          className={`badge ${entry.public ? 'public' : 'private'}`}
          data-testid={`mbtiles-public-${entry.id}`}
        >
          {entry.public ? 'public' : 'private'}
        </span>
      </td>
      <td>{entry.file}</td>
      <td>
        <div className="row">
          <button
            className="btn ghost"
            onClick={togglePublic}
            disabled={busy}
            data-testid={`mbtiles-toggle-${entry.id}`}
          >
            {entry.public ? '비공개로' : '공개로'}
          </button>
          <button
            className="btn danger"
            onClick={remove}
            disabled={busy}
            data-testid={`mbtiles-delete-${entry.id}`}
          >
            삭제
          </button>
        </div>
      </td>
    </tr>
  );
}
